import type { TaskPriority } from '../types';

export function escalatedPriority(
  base: TaskPriority,
  dueDate: string | undefined,
  now: Date = new Date()
): TaskPriority {
  if (!dueDate) return base;
  // Only escalate `low` — users who explicitly pick `medium` or `high` have
  // already chosen the signal they want; overriding that just made the UI
  // feel broken when someone downgraded a high-priority task to medium.
  if (base !== 'low') return base;
  const due = new Date(dueDate).getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilDue = (due - now.getTime()) / msPerDay;
  if (daysUntilDue <= 1) return 'high';
  if (daysUntilDue <= 3) return 'medium';
  return base;
}

export function priorityRank(p: TaskPriority): number {
  return p === 'high' ? 0 : p === 'medium' ? 1 : 2;
}

export function priorityColor(p: TaskPriority): string {
  return p === 'high' ? '#ef4444' : p === 'medium' ? '#eab308' : '#9ca3af';
}

/** Priority color for the task-row left stripe. Low = transparent so only
 *  medium+high rows visually pop. Escalated priorities pass through the
 *  already-escalated value. */
export function priorityStripeColor(p: TaskPriority): string {
  if (p === 'high') return '#ef4444';
  if (p === 'medium') return '#f59e0b';
  return 'transparent';
}

export function priorityLabel(p: TaskPriority): string {
  return p === 'high' ? 'High' : p === 'medium' ? 'Medium' : 'Low';
}

export function buildPreview(source: {
  forwardNote?: string;
  originalBodyText?: string;
}): string | null {
  if (source.forwardNote && source.forwardNote.trim()) {
    return source.forwardNote.trim();
  }
  const body = source.originalBodyText ?? '';
  const cleaned = body
    .replace(/^[>\s]*On .+ wrote:$/gm, '')
    .replace(/^--\s*$[\s\S]*/m, '')
    .replace(/^\s*Sent from my .+$/gm, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
  if (!cleaned) return null;
  return cleaned.length > 200 ? cleaned.slice(0, 197).trim() + '…' : cleaned;
}
